import { type RefObject, useCallback, useRef, useState } from 'react';
import {
  type AudioDeviceOption,
  listAudioDevices,
  resolveDeviceId,
} from './audioDevices.ts';
import {
  rememberChatMicrophoneId,
  storedChatMicrophoneId,
} from './chatDictationPrefs.ts';
import { isMicrophoneBlocked } from './roleplayCall.ts';
import { reportError } from './reportError.ts';

export interface DictationMicrophones {
  /** What can be recorded with; empty until the browser will name its devices. */
  microphones: AudioDeviceOption[];
  microphoneId: string | null;
  /**
   * The chosen microphone as a recording needs it: read at the moment the
   * device is opened rather than closed over, so that picking another one does
   * not hand the composer a fresh `start` to re-render around.
   */
  chosen: RefObject<string | null>;
  choose: (deviceId: string) => void;
  /** Ask for the microphone so the devices can be named. Does nothing once they are. */
  request: () => void;
  /** Read the devices again, keeping the chosen one where it still exists. */
  refresh: () => Promise<void>;
}

/**
 * Which microphone dictation records with, remembered between visits: it is an
 * answer somebody gives once, and asking again every visit would be the whole
 * reason they stop using it.
 */
export function useDictationMicrophones(onBlocked: () => void): DictationMicrophones {
  const [microphones, setMicrophones] = useState<AudioDeviceOption[]>([]);
  const [microphoneId, setMicrophoneId] = useState<string | null>(storedChatMicrophoneId);
  /*
   * The devices as they stand, so asking for them can tell whether they are
   * already named without changing every time the list does.
   */
  const known = useRef<AudioDeviceOption[]>([]);
  /* Whether the browser is being asked right now: two opens are one question. */
  const asking = useRef(false);
  const chosen = useRef(microphoneId);

  /*
   * A browser that will not enumerate its own devices — an old one, or one
   * behind a policy — leaves the list empty rather than the composer broken:
   * the recording runs on whatever the default device is.
   */
  const refresh = useCallback(async () => {
    const devices = await listAudioDevices().catch(() => ({
      microphones: [],
      speakers: [],
    }));

    const resolved = resolveDeviceId(chosen.current, devices.microphones);
    chosen.current = resolved;
    known.current = devices.microphones;
    setMicrophones(devices.microphones);
    setMicrophoneId(resolved);
  }, []);

  const choose = useCallback((deviceId: string) => {
    chosen.current = deviceId;
    setMicrophoneId(deviceId);
    rememberChatMicrophoneId(deviceId);
  }, []);

  /*
   * Names, not devices: a browser lists what it has to any page but names it
   * only to one it has already trusted with a recording. So the microphone is
   * asked for when somebody goes looking for the list — a granted permission
   * is all that is wanted from the stream, and it is let go of at once.
   */
  const request = useCallback(() => {
    if (asking.current || known.current.some((device) => device.label !== '')) {
      return;
    }

    asking.current = true;

    void (async () => {
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        await refresh();
      } catch (caught) {
        if (isMicrophoneBlocked(caught)) {
          onBlocked();
          return;
        }

        reportError(caught, { what: 'chat-dictation-devices' });
      } finally {
        stream?.getTracks().forEach((track) => track.stop());
        asking.current = false;
      }
    })();
  }, [refresh, onBlocked]);

  return { microphones, microphoneId, chosen, choose, request, refresh };
}
